const ACTIVE_MEMBERSHIP_STATUSES = new Set(['Active', 'Trialing']);

/**
 * Returns true when the selected booking date is on or after the membership's
 * current period end date (date-only ISO comparison).
 */
export function isMembershipRenewalRisk(selectedDateIso, currentPeriodEndUtc) {
  if (!selectedDateIso || !currentPeriodEndUtc) return false;
  return selectedDateIso >= String(currentPeriodEndUtc).slice(0, 10);
}

/**
 * Returns the price to display for a membership plan at booking time.
 * 0 when the customer has an active/trialing membership for this plan; otherwise plan.price.
 */
export function getMembershipDisplayPrice(plan, customerMemberships) {
  if (!plan) return null;
  const activeMembership = (customerMemberships || []).find(
    (m) => m.membershipPlanId === plan.id && ACTIVE_MEMBERSHIP_STATUSES.has(m.status)
  );
  return activeMembership ? 0 : plan.price;
}

/**
 * Finds the first active/trialing membership whose plan includes the service.
 */
export function findCoveringMembershipForService(serviceId, customerMemberships, membershipPlans) {
  if (serviceId == null) return null;

  for (const membership of customerMemberships || []) {
    if (!ACTIVE_MEMBERSHIP_STATUSES.has(membership.status)) continue;
    const plan = (membershipPlans || []).find(
      (p) => p.id === membership.membershipPlanId && (p.serviceIds || []).includes(serviceId)
    );
    if (plan) {
      return { membership, plan };
    }
  }

  return null;
}

/**
 * Filters team members by optional plan-allowed IDs. Empty/null allowed set = no restriction.
 */
export function filterTeamMembersByPlan(teamMembers, planTeamMemberIds) {
  const list = teamMembers || [];
  if (!planTeamMemberIds?.length) return list;
  const allowed = new Set(planTeamMemberIds);
  return list.filter((m) => allowed.has(m.id));
}

/**
 * Returns store plans the customer currently holds (Active / Trialing).
 */
export function getActiveMembershipPlans(membershipPlans, customerMemberships) {
  return (membershipPlans || []).filter((plan) =>
    (customerMemberships || []).some(
      (membership) =>
        membership.membershipPlanId === plan.id
        && ACTIVE_MEMBERSHIP_STATUSES.has(membership.status)
    )
  );
}

/**
 * Resolves period-end UTC for the membership currently selected as payment method.
 */
export function getSelectedMembershipPeriodEndUtc(selectedMembershipId, bookingEntitlements, customerMemberships) {
  if (!selectedMembershipId) return null;

  const fromEntitlement = (bookingEntitlements || []).find(
    (e) => e.customerMembershipId === selectedMembershipId
  );
  if (fromEntitlement?.currentPeriodEndUtc) {
    return fromEntitlement.currentPeriodEndUtc;
  }

  const fromMembership = (customerMemberships || []).find((m) => m.id === selectedMembershipId);
  return fromMembership?.currentPeriodEndUtc ?? null;
}

export function resolveMembershipPaidThroughUtc(membership) {
  const currentEnd = membership?.currentPeriodEndUtc || null;
  const paidThrough = membership?.manuallyPaidThroughUtc || null;
  if (!currentEnd) {
    return paidThrough;
  }

  if (!paidThrough) {
    return currentEnd;
  }

  return new Date(paidThrough).getTime() > new Date(currentEnd).getTime()
    ? paidThrough
    : currentEnd;
}

export { ACTIVE_MEMBERSHIP_STATUSES };
